import * as React from 'react';
import { createRef, useState } from 'react';

import { Button, Stack, TextField } from '@mui/material';
import styles from './RegisterbeteckningInput.module.css';

interface RegisterbeteckningInputProps {
  registerbeteckningar: string[];
  setRegisterbeteckningar: (value: string[]) => void;
}

export const RegisterbeteckningInput: React.FC<
  RegisterbeteckningInputProps
> = ({ registerbeteckningar, setRegisterbeteckningar }) => {
  const inputRef = createRef<HTMLInputElement>();
  const [input, setInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  React.useEffect(
    () => validateInput(sanitizeInput(input)),
    [registerbeteckningar]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.currentTarget.value);
    validateInput(sanitizeInput(e.currentTarget.value));
  };

  const validateInput = (str: string) => {
    if (containsSpecialChars(str)) {
      setErrorMessage(
        'Fältet får endast innehålla bokstäver, siffror, bindestreck och kolon.'
      );
    } else if (containsDuplicates(str)) {
      setErrorMessage('Fastigheten är redan tillagd.');
    } else {
      setErrorMessage('');
    }
  };

  const sanitizeInput = (input: string) => input.replaceAll(/\s+/g, ' ').trim();

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Enter' &&
      input.trim() !== '' &&
      !containsDuplicates(input) &&
      !containsSpecialChars(input)
    ) {
      handleLaggTill();
    }
  };

  const handleLaggTill = () => {
    setRegisterbeteckningar([...registerbeteckningar, sanitizeInput(input)]);
    setInput('');
    inputRef.current.focus();
  };

  const containsSpecialChars = (str: string): boolean =>
    !/^[0-9a-zA-ZåäöÅÄÖ\-:\s]*$/.test(str);

  const containsDuplicates = (str: string): boolean =>
    registerbeteckningar.some(
      (fastighet) => str.toLocaleLowerCase() === fastighet.toLocaleLowerCase()
    );

  return (
    <Stack direction="row" sx={{ ml: 2.5, mt: 2, mb: 2.5 }} spacing={1}>
      <TextField
        sx={{ width: 500 }}
        size="small"
        label={'Fastighetsbeteckning eller UUID'}
        value={input}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        spellCheck={false}
        helperText={errorMessage}
        FormHelperTextProps={{
          className: styles.helperText,
        }}
        autoComplete="off"
        ref={inputRef}
      />
      <Button
        onClick={handleLaggTill}
        disabled={
          input.length === 0 ||
          containsSpecialChars(input) ||
          containsDuplicates(input)
        }
      >
        Lägg till
      </Button>
    </Stack>
  );
};
